<template>
  <div class="pct">
    <!-- live totals — computed from the same rows the table shows -->
    <div class="pct-summary">
      <div class="pct-chip is-paid">
        <i class="fas fa-check-circle"></i>
        <div>
          <span class="pct-chip__val tabular">{{ convertToEURO(paidTotal) }}</span>
          <span class="pct-chip__label">{{ msg('Paid') }}</span>
        </div>
      </div>
      <div class="pct-chip is-open">
        <i class="fas fa-hourglass-half"></i>
        <div>
          <span class="pct-chip__val tabular">{{ convertToEURO(openTotal) }}</span>
          <span class="pct-chip__label">{{ msg('Not paid') }}</span>
        </div>
      </div>
      <div class="pct-chip">
        <i class="far fa-credit-card"></i>
        <div>
          <span class="pct-chip__val tabular">{{ (payments || []).length }}</span>
          <span class="pct-chip__label">{{ msg('Payments') }}</span>
        </div>
      </div>
    </div>

    <b-table
      :items="payments"
      :fields="fields"
      class="pct-table"
      hover
      responsive
      thead-class="pct-thead"
    >
      <template #cell(type)="data">
        <span class="pct-type"><i :class="typeIcon(data.value)" class="mr-25"></i>{{ msg(data.value) }}</span>
      </template>
      <template #cell(reason)="data">
        <span class="pct-reason">{{ data.value }}</span>
      </template>
      <template #cell(paymentDate)="data">
        <span class="text-nowrap">{{ formatDate(data.value) }}</span>
      </template>
      <template #cell(dateToCharge)="data">
        <span class="text-nowrap">{{ formatDateOnly(data.value) }}</span>
      </template>
      <template #cell(amount)="data">
        <span class="pct-amount tabular">{{ convertToEURO(data.value) }}</span>
      </template>
      <template #cell(paid)="data">
        <b-badge pill :variant="data.value ? 'light-success' : 'light-danger'">
          <i :class="data.value ? 'fas fa-check' : 'fas fa-clock'" class="mr-25"></i>
          {{ data.value ? msg('Yes') : msg('No') }}
        </b-badge>
      </template>
      <template #cell(brand)="data">
        <span v-if="data.value != null && data.value !== ''" class="pct-card text-nowrap">
          <i :class="brandIcon(data.value)" class="mr-25"></i>{{ data.value }} <span class="pct-card__digits">•••• {{ data.item.digits }}</span>
        </span>
      </template>
      <template #cell(id)="row">
        <div v-if="row.item.id != null" class="text-nowrap text-center">
          <b-button
            :disabled="row.item.paid"
            size="sm"
            variant="flat-success"
            class="pct-action"
            v-b-tooltip
            :title="msg('Mark as paid')"
            @click="markAsPaid(row.item, row.index, $event.target)"
          >
            <i class="fas fa-check"></i>
          </b-button>
          <b-button
            :disabled="row.item.paid || isChargeDatePassed(row.item.dateToCharge)"
            size="sm"
            variant="flat-warning"
            class="pct-action"
            v-b-tooltip
            :title="msg('Reminder mail')"
            @click="sendReminderMail(row.item, row.index, $event.target)"
          >
            <i class="fas fa-bell"></i>
          </b-button>
        </div>
      </template>
    </b-table>

    <p v-if="(payments || []).length === 0" class="pct-empty">
      <i class="far fa-credit-card mr-50"></i>{{ msg('Payments') }}: 0
    </p>
  </div>
</template>

<script>
import {mapActions} from "vuex";

export default {
  name: 'PaymentClientTable',
  props: {
    title: {
      type: String,
      default: 'Payments'
    },
    contractId: {
      type: String,
      default: null,
      required: true
    },
    payments: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      fields: null
    };
  },
  created() {
    this.setFields();
  },
  computed: {
    paidTotal() {
      return (this.payments || []).filter(p => p != null && p.paid).reduce((sum, p) => sum + (p.amount || 0), 0);
    },
    openTotal() {
      return (this.payments || []).filter(p => p != null && !p.paid).reduce((sum, p) => sum + (p.amount || 0), 0);
    },
  },
  methods: {
    ...mapActions("realEstateContracts", ["markPaymentAsPaid", "sendReminderMailPayment"]),

    setFields() {
      this.fields = [
        {key: 'type', label: this.msg('Type')},
        {key: 'reason', label: this.msg('Reason')},
        {key: 'amount', label: this.msg('Amount'), class: 'text-right'},
        {key: 'paid', label: this.msg('Paid'), class: 'text-center'},
        {key: 'brand', label: this.msg('Card')},
        {key: 'paymentDate', label: this.msg('Creationdate')},
        {key: 'dateToCharge', label: this.msg('Date for next payment')},
        {key: 'id', label: this.msg('Actions'), class: 'text-center'}
      ];
    },
    typeIcon(type) {
      const normalized = (type || '').toLowerCase();
      if (normalized.indexOf('cash') >= 0) return 'fas fa-money-bill-wave';
      if (normalized.indexOf('wire') >= 0) return 'fas fa-university';
      return 'far fa-credit-card';
    },
    brandIcon(brand) {
      const normalized = (brand || '').toLowerCase();
      if (normalized.indexOf('visa') >= 0) return 'fab fa-cc-visa';
      if (normalized.indexOf('master') >= 0) return 'fab fa-cc-mastercard';
      if (normalized.indexOf('amex') >= 0 || normalized.indexOf('american') >= 0) return 'fab fa-cc-amex';
      return 'far fa-credit-card';
    },
    formatDate(date) {
      if (!date) return '';
      const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false // Ensure 24-hour format
      };

      const formattedDate = new Date(date).toLocaleString('de-AT', options);
      return formattedDate.replace(',', ''); // Remove comma (optional)
    },
    formatDateOnly(date) {
      if (!date) return '';
      const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      };

      const formattedDate = new Date(date).toLocaleString('de-AT', options);
      return formattedDate.replace(',', ''); // Remove comma (optional)
    },
    convertToEURO(someNumber) {
      return new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
      }).format(someNumber);
    },
    isChargeDatePassed(chargeDate) {
      if (!chargeDate) return false; // or true, depending on desired default behavior
      const today = new Date().toISOString().split('T')[0]; // "yyyy-MM-dd"
      return chargeDate > today; // returns true if chargeDate has passed
    },
    markAsPaid(payment, index, target) {
      let $this = this;
      this.markPaymentAsPaid({
        contractId: this.contractId,
        paymentId: payment.id,
      }).then(approve => {
        $this.$emit('submitted')
      })
    },
    sendReminderMail(payment, index, target) {
      let $this = this;
      this.sendReminderMailPayment({
        contractId: this.contractId,
        paymentId: payment.id,
      }).then(approve => {
        $this.$emit('submitted')
      })
    },
  }
};
</script>

<style lang="scss">
.pct {
  .pct-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .pct-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border-radius: 12px;
    border: 1px solid rgba(34, 41, 47, .07);
    background: rgba(34, 41, 47, .015);

    > i { font-size: 15px; opacity: .8; }
    > div { display: flex; flex-direction: column; }

    &__val { font-weight: 800; font-size: 13.5px; line-height: 1.2; }
    &__label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; opacity: .55; }

    &.is-paid { border-color: rgba(40, 199, 111, .25); background: rgba(40, 199, 111, .07); > i { color: #28c76f; } }
    &.is-open { border-color: rgba(255, 159, 67, .3); background: rgba(255, 159, 67, .08); > i { color: #ff9f43; } }
  }

  .pct-table {
    font-size: 12.5px;
    border-radius: 12px;
    overflow: hidden;

    thead th {
      font-size: 10.5px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: .35px;
      background: rgba(34, 41, 47, .03) !important;
      border: none;
      white-space: nowrap;
    }
    td { vertical-align: middle; border-color: rgba(34, 41, 47, .05); }
  }

  .pct-amount { font-weight: 800; white-space: nowrap; }
  .pct-type { white-space: nowrap; font-weight: 600; }
  .pct-reason { word-break: break-word; }
  .pct-card__digits { font-weight: 700; opacity: .7; }

  .pct-action {
    width: 30px; height: 30px; padding: 0 !important;
    border-radius: 9px !important;
    display: inline-flex; align-items: center; justify-content: center;
  }

  .pct-empty {
    text-align: center;
    padding: 12px 0 4px;
    font-size: 12px;
    opacity: .55;
    margin: 0;
  }
}

.dark-layout .pct {
  .pct-chip {
    border-color: rgba(255, 255, 255, .08);
    background: rgba(255, 255, 255, .03);

    &.is-paid { border-color: rgba(40, 199, 111, .3); background: rgba(40, 199, 111, .1); }
    &.is-open { border-color: rgba(255, 159, 67, .3); background: rgba(255, 159, 67, .1); }
  }

  .pct-table {
    thead th { background: rgba(255, 255, 255, .04) !important; }
    td { border-color: rgba(255, 255, 255, .05); }
  }
}
</style>
